import { Link } from '@tanstack/react-router'
import type { InvoiceReminderResponseDto } from '@trellis/api'
import { Button } from '@trellis/roots/button'
import { DetailCard, UpcomingReminders } from '@trellis/roots/data'
import type { UpcomingReminder, UpcomingRemindersGroupBy } from '@trellis/roots/data'
import { Skeleton } from '@trellis/roots/skeleton'
import { Typography } from '@trellis/roots/typography'
import { Bell, Square } from 'lucide-react'
import { useState } from 'react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'

import { useOrganizationToday } from '@/hooks/useOrganization'
import {
  useInvoiceRemindersQuery,
  useRestoreInvoiceRemindersMutation,
  useStopInvoiceRemindersMutation,
  useUpdateInvoiceReminderMutation,
} from '@/hooks/useReminders'
import type { InvoiceRemindersListParams } from '@/requests/reminders'

import { ReminderChannelBadge } from './ReminderChannelBadge'
import { SendReminderNowDialog } from './SendReminderNowDialog'
import type { SendReminderNowTarget } from './SendReminderNowDialog'

const UPCOMING_STATUSES: InvoiceRemindersListParams['status'] = ['SCHEDULED', 'DISMISSED']
const MAX_REMINDERS = 500
const PAUSED_REASON = 'Paused: a reminder was sent in the last 24 hours'

const pluralReminders = (count: number) =>
  count === 1 ? '1 reminder' : `${count} reminders`

const toUpcoming = (reminders: InvoiceReminderResponseDto[]): UpcomingReminder[] =>
  reminders.flatMap((reminder) =>
    reminder.status === 'SCHEDULED' || reminder.status === 'DISMISSED'
      ? [{ ...reminder, status: reminder.status }]
      : [],
  )

interface UpcomingRemindersCardProps {
  title: string
  /** Narrows the list: one invoice, one client, or everything up to a date. */
  filters: Pick<InvoiceRemindersListParams, 'invoiceId' | 'clientId' | 'to'>
  groupBy: UpcomingRemindersGroupBy
  /**
   * `card` wraps the list in one card. `invoice-cards` (with `groupBy="invoice"`) drops
   * that wrapper so each invoice's reminders sit in a card of their own, full width.
   * `bare` renders just the list (and its dialogs) for a caller that supplies the card,
   * so `title`, `description` and `headerAction` are unused.
   */
  layout?: 'card' | 'invoice-cards' | 'bare'
  /** Rows per page (invoices per page when grouped by invoice); unset lists everything. */
  pageSize?: number
  /** Subtitle under the title; a function receives the reminders shown, e.g. for a count. */
  description?: ReactNode | ((reminders: UpcomingReminder[]) => ReactNode)
  /** Header action, e.g. an "Edit schedule" link. */
  headerAction?: ReactNode
  /** Sits beside the title in the `invoice-cards` layout, e.g. an info tooltip. */
  titleAccessory?: ReactNode
  /** Leave alert-only steps out (Overview only lists emails going to clients). */
  emailOnly?: boolean
  /** Render nothing while there is nothing scheduled (the invoice sidebar). */
  hideWhenEmpty?: boolean
  /** Adds "Stop all" to the header; for the single-invoice sidebar. */
  stopAllInvoice?: { id: string; invoiceNumber: string }
  /** Invoice page: Send now stays paused until this instant (24 hours after the last). */
  sendNowPausedUntil?: string | null
  /** Invoice page only: lets the Send now dialog name the real recipient. */
  clientEmail?: string | null
  /** Shown above the list, e.g. the 24-hour notice. */
  notice?: (isPaused: boolean) => ReactNode
  emptyMessage?: string
}

/**
 * Smart wrapper around the roots {@link UpcomingReminders} list: fetches reminders and
 * owns dismiss/restore/stop with Undo toasts and the Send now confirmation.
 */
export function UpcomingRemindersCard({
  title,
  filters,
  groupBy,
  layout = 'card',
  pageSize,
  description,
  headerAction,
  titleAccessory,
  emailOnly = false,
  hideWhenEmpty = false,
  stopAllInvoice,
  sendNowPausedUntil,
  clientEmail,
  notice,
  emptyMessage,
}: UpcomingRemindersCardProps) {
  const today = useOrganizationToday()
  const remindersQuery = useInvoiceRemindersQuery({
    ...filters,
    status: UPCOMING_STATUSES,
    limit: MAX_REMINDERS,
  })
  const updateMutation = useUpdateInvoiceReminderMutation()
  const stopMutation = useStopInvoiceRemindersMutation()
  const restoreMutation = useRestoreInvoiceRemindersMutation()
  const [sendTarget, setSendTarget] = useState<SendReminderNowTarget | null>(null)
  const [pausedInvoiceIds, setPausedInvoiceIds] = useState<ReadonlySet<string>>(new Set())

  const reminders = toUpcoming(remindersQuery.data?.data ?? []).filter(
    (reminder) => !emailOnly || reminder.emailClient,
  )
  const isPausedByProp =
    sendNowPausedUntil !== undefined &&
    sendNowPausedUntil !== null &&
    new Date(sendNowPausedUntil).getTime() > Date.now()
  const isInvoicePaused = (invoiceId: string) =>
    isPausedByProp || pausedInvoiceIds.has(invoiceId)
  const hasScheduled = reminders.some((reminder) => reminder.status === 'SCHEDULED')

  if (hideWhenEmpty && remindersQuery.isSuccess && reminders.length === 0) return null

  function nextScheduledEmail(invoiceId: string): string | null {
    return (
      reminders
        .filter(
          (reminder) =>
            reminder.invoiceId === invoiceId &&
            reminder.status === 'SCHEDULED' &&
            reminder.emailClient,
        )
        .map((reminder) => reminder.scheduledDate)
        .sort()[0] ?? null
    )
  }

  async function restore(reminderIds: string[], successMessage: string) {
    try {
      await restoreMutation.mutateAsync(reminderIds)
      toast.success(successMessage)
    } catch (caughtError) {
      toast.error('Failed to restore', {
        description: caughtError instanceof Error ? caughtError.message : undefined,
      })
    }
  }

  async function handleDismiss(reminder: UpcomingReminder) {
    try {
      await updateMutation.mutateAsync({ reminderId: reminder.id, status: 'DISMISSED' })
      toast.success('Reminder dismissed', {
        action: {
          label: 'Undo',
          onClick: () => void restore([reminder.id], 'Reminder restored'),
        },
      })
    } catch (caughtError) {
      toast.error('Failed to dismiss reminder', {
        description: caughtError instanceof Error ? caughtError.message : undefined,
      })
    }
  }

  async function handleRestore(reminder: UpcomingReminder) {
    try {
      await updateMutation.mutateAsync({ reminderId: reminder.id, status: 'SCHEDULED' })
      toast.success('Reminder restored')
    } catch (caughtError) {
      toast.error('Failed to restore reminder', {
        description: caughtError instanceof Error ? caughtError.message : undefined,
      })
    }
  }

  async function handleStopAll(invoiceId: string) {
    const invoiceNumber =
      reminders.find((reminder) => reminder.invoiceId === invoiceId)?.invoiceNumber ?? ''
    try {
      const { dismissedReminderIds } = await stopMutation.mutateAsync(invoiceId)
      toast.success(
        `Stopped ${pluralReminders(dismissedReminderIds.length)} for ${invoiceNumber}`,
        {
          action: {
            label: 'Undo',
            onClick: () => void restore(dismissedReminderIds, 'Reminders restored'),
          },
        },
      )
    } catch (caughtError) {
      toast.error('Failed to stop reminders', {
        description: caughtError instanceof Error ? caughtError.message : undefined,
      })
    }
  }

  function handleSendNow(reminder: UpcomingReminder) {
    setSendTarget({
      invoiceId: reminder.invoiceId,
      invoiceNumber: reminder.invoiceNumber,
      clientName: reminder.clientName,
      clientEmail,
      nextReminderDate: nextScheduledEmail(reminder.invoiceId),
    })
  }

  const isPaused = stopAllInvoice ? isInvoicePaused(stopAllInvoice.id) : false

  const headerActionContent =
    stopAllInvoice && hasScheduled ? (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-destructive-text h-7 rounded-full px-2.5 text-xs"
        disabled={stopMutation.isPending}
        onClick={() => void handleStopAll(stopAllInvoice.id)}
      >
        <Square className="size-3 fill-current" aria-hidden="true" />
        Stop all
      </Button>
    ) : (
      headerAction
    )
  const descriptionContent =
    typeof description === 'function' ? description(reminders) : description
  const isInvoiceCards = layout === 'invoice-cards'
  const list = remindersQuery.isPending ? (
    <div
      role="status"
      aria-label={`Loading ${title.toLowerCase()}`}
      className="space-y-2"
    >
      <Skeleton variant="text" className="w-2/3" />
      <Skeleton variant="text" className="w-1/2" />
    </div>
  ) : (
    <UpcomingReminders
      reminders={reminders}
      today={today}
      groupBy={groupBy}
      pageSize={pageSize}
      invoiceLayout={isInvoiceCards ? 'cards' : 'sections'}
      linkComponent={Link}
      renderChannelBadge={(reminder) => <ReminderChannelBadge reminder={reminder} />}
      getInvoiceHref={(invoiceId) => `/invoices/${invoiceId}`}
      getClientHref={(clientId) => `/clients/${clientId}`}
      getSendNowBlockedReason={(reminder) =>
        isInvoicePaused(reminder.invoiceId) ? PAUSED_REASON : null
      }
      onSendNow={handleSendNow}
      onDismiss={(reminder) => void handleDismiss(reminder)}
      onRestore={(reminder) => void handleRestore(reminder)}
      onStopAll={
        groupBy === 'invoice' ? (invoiceId) => void handleStopAll(invoiceId) : undefined
      }
      emptyMessage={emptyMessage}
    />
  )

  return (
    <>
      {layout === 'bare' ? (
        <div className="flex flex-col gap-3">
          {notice?.(isPaused)}
          {list}
        </div>
      ) : isInvoiceCards ? (
        <section className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-2.5">
              <Bell
                className="text-muted-foreground mt-1 size-5 shrink-0"
                aria-hidden="true"
              />
              <div>
                <div className="flex items-center gap-1.5">
                  <Typography variant="h5" as="h2">
                    {title}
                  </Typography>
                  {titleAccessory}
                </div>
                {descriptionContent && (
                  <Typography variant="body-sm" color="muted" as="div">
                    {descriptionContent}
                  </Typography>
                )}
              </div>
            </div>
            {headerActionContent}
          </div>
          {notice?.(isPaused)}
          {list}
        </section>
      ) : (
        <DetailCard
          title={title}
          icon={Bell}
          description={descriptionContent}
          headerAction={headerActionContent}
          contentClassName="flex flex-col gap-3 p-4"
        >
          {notice?.(isPaused)}
          {list}
        </DetailCard>
      )}
      <SendReminderNowDialog
        target={sendTarget}
        onOpenChange={(isOpen) => {
          if (!isOpen) setSendTarget(null)
        }}
        onPaused={(invoiceId) =>
          setPausedInvoiceIds((previous) => new Set(previous).add(invoiceId))
        }
      />
    </>
  )
}
